import { API_BASE_URL, ApiClient } from "./client";

export type ChatEvent =
  | { type: "agent_chunk"; text: string }
  | { type: "agent_thought"; text: string }
  | { type: "tool_start"; tool_call_id?: string | null; title?: string | null; kind?: string }
  | { type: "tool_progress"; tool_call_id?: string | null; delta?: unknown }
  | { type: "tool_end"; tool_call_id?: string | null; status?: string; raw?: unknown }
  | { type: "plan"; entries?: unknown[] }
  | {
      type: "context_usage";
      context_size?: number;
      context_used?: number;
      cost_usd?: number;
      cost_currency?: string;
    }
  | { type: "done"; usage?: Record<string, number> }
  | { type: "cancelled" }
  | { type: "error"; message?: string; [key: string]: unknown }
  | { type: "unknown"; kind?: string; raw?: unknown }
  | { type: string; [key: string]: unknown };

export interface StreamChatOptions {
  api: ApiClient;
  appId: string;
  sessionId: string;
  prompt: string;
  signal?: AbortSignal;
  onEvent: (event: ChatEvent) => void;
}

export async function streamChat(options: StreamChatOptions): Promise<void> {
  const response = await options.api.authorizedFetch(`/v1/apps/${options.appId}/chat`, {
    method: "POST",
    body: JSON.stringify({
      session_id: options.sessionId,
      prompt: options.prompt,
    }),
    signal: options.signal,
  });

  if (!response.ok) {
    throw new Error(await readStreamError(response));
  }

  if (!response.body) {
    throw new Error("stream_body_missing");
  }

  await parseSseStream(response.body, options.onEvent);
}

async function parseSseStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: ChatEvent) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      if (buffer.trim()) {
        emitSseBlock(buffer, onEvent);
      }
      return;
    }

    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() || "";
    for (const block of blocks) {
      emitSseBlock(block, onEvent);
    }
  }
}

function emitSseBlock(block: string, onEvent: (event: ChatEvent) => void): void {
  const lines = block.split(/\r?\n/);
  const dataLines: string[] = [];

  for (const line of lines) {
    if (!line || line.startsWith(":")) {
      continue;
    }
    if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  if (dataLines.length === 0) {
    return;
  }

  const rawData = dataLines.join("\n");
  try {
    onEvent(JSON.parse(rawData) as ChatEvent);
  } catch {
    onEvent({ type: "unknown", raw: rawData });
  }
}

async function readStreamError(response: Response): Promise<string> {
  try {
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const json = (await response.json()) as { detail?: unknown };
      return typeof json.detail === "string" ? json.detail : JSON.stringify(json.detail ?? json);
    }
    return await response.text();
  } catch {
    return `${API_BASE_URL} stream failed with ${response.status}`;
  }
}
